import { Box, Container, Stack } from '@mui/material';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { addLocalePrefix } from 'utils/routes.utils';

import { Routes } from 'constants/routes.constants';
import { useLocale } from 'hooks/useLocale';
import { useAccount } from 'restful/accounts';
import { useTicketsOverviewView } from 'services/datalayer/tickets';

import Authentication from 'components/@account/Authentication';
import DownloadApp from 'components/@account/DownloadApp';
import UpcomingEvents from 'components/@account/UpcomingEvents';
import LoadingScreen from 'components/@common/LoadingScreen';
import PageBreadcrumbs from 'components/@navigation/PageBreadcrumbs';

import EmptyScreen from './EmptyScreen';
import { useAccountTickets } from './hooks';
import PastEvents from './PastEvents';

const AccountTicketsContainer = () => {
  const trackTicketsOverview = useTicketsOverviewView();
  const { isLoading: isLoadingUser, isSignedIn } = useAccount();
  const {
    isLoadingEvents,
    isGeneratingTickets,
    upcomingEvents,
    pastEvents,
    status,
    generatingTicketsStatus,
  } = useAccountTickets();
  const { locale } = useLocale();

  useEffect(() => {
    if (
      !isLoadingUser &&
      !isLoadingEvents &&
      status === 'success' &&
      generatingTicketsStatus === 'success' &&
      !isGeneratingTickets
    ) {
      trackTicketsOverview(`${upcomingEvents.length}`);
    }
  }, [
    isGeneratingTickets,
    isLoadingUser,
    status,
    isLoadingEvents,
    trackTicketsOverview,
    upcomingEvents.length,
    generatingTicketsStatus,
  ]);

  if (isLoadingUser) {
    return <LoadingScreen />;
  }

  return (
    <>
      <PageBreadcrumbs
        items={[{ label: <FormattedMessage id="account.tickets.label" /> }]}
        hideOnMobile
      />
      <Container
        sx={{
          mt: { xs: 2, md: 3 },
          mb: { xs: 5, md: 15 },
          flex: 1,
          display: 'flex',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '6fr 1fr 5fr' },
            gridTemplateRows: { xs: 'auto 1fr', md: 'auto' },

            gridTemplateAreas: {
              xs: '"sidebar" "events"',
              md: '"events . sidebar"',
            },

            alignItems: 'start',
            flex: 1,
            gap: { xs: 4, md: 2 },
          }}
        >
          <Stack
            sx={{
              gap: 8,
              gridArea: 'events',
              flex: 1,
            }}
          >
            {isSignedIn ? (
              <>
                {!!upcomingEvents.length ||
                !!pastEvents.length ||
                isLoadingEvents ? (
                  <>
                    <UpcomingEvents
                      events={upcomingEvents}
                      isLoading={isLoadingEvents}
                      isGeneratingTickets={isGeneratingTickets}
                    />
                    <PastEvents events={pastEvents} />
                  </>
                ) : (
                  <EmptyScreen />
                )}
              </>
            ) : (
              <Authentication
                titleIntlId="account.sign.in.tickets.label"
                afterSignInUrl={addLocalePrefix(locale, Routes.AccountTickets)}
                originSourceCategory="Tickets"
              />
            )}
          </Stack>

          {isSignedIn && (
            <DownloadApp
              sx={{
                gridArea: 'sidebar',
              }}
            />
          )}
        </Box>
      </Container>
    </>
  );
};

export default AccountTicketsContainer;
